// File: BigDataMap.utils.ts

import { Feature, FeatureCollection, Geometry, Point, Polygon, MultiPolygon, GeoJsonProperties, BBox } from "geojson";
import {
  SafeGeoJsonProperties,
  SafeFeature,
  ClusterCountsWithTotal,
  ClusterCounts,
  ExportMapData,
  ViewStateType,
} from "@/types/BigDataMap.types";
import { WebMercatorViewport } from "@deck.gl/core";
import { bbox } from "@turf/bbox"; // Ensure installed: npm install @turf/bbox


import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import * as d3 from "d3";

// Utility function to check if geoData is SafeFeatureCollection
export const isSafeFeatureCollection = (
  geoData: Feature<Geometry, GeoJsonProperties> | FeatureCollection<Geometry, GeoJsonProperties>
): geoData is FeatureCollection<Geometry, SafeGeoJsonProperties> => {
  return geoData.type === "FeatureCollection";
};

// Utility function to get feature ID
export const getFeatureId = (feature: SafeFeature<Geometry>): string | null => {
  if (feature.properties?.type === "state" && feature.id !== undefined) {
    return `state-${feature.id}`;
  } else if (feature.properties?.type === "county" && feature.id !== undefined) {
    return `county-${feature.id}`;
  }
  return null;
};

/**
 * Calculates the number of points within the given feature's geometry, grouped by cluster,
 * and the total number of points within the boundary.
 *
 * @param feature - The GeoJSON feature (Polygon or MultiPolygon).
 * @param points - The array of data points.
 * @param filterCluster - Optional filter to count points only from a specific cluster.
 * @returns An object mapping cluster numbers to counts and the total point count, or null if invalid geometry.
 */
export const calculatePointsInBoundary = (
  feature: Feature<Geometry, GeoJsonProperties>,
  points: ExportMapData[],
  filterCluster?: number
): ClusterCountsWithTotal | null => {
  // Ensure the feature is a Polygon or MultiPolygon
  if (feature.geometry.type !== "Polygon" && feature.geometry.type !== "MultiPolygon") {
    console.error("Feature must be a Polygon or MultiPolygon.");
    return null;
  }

  const polygon = feature as Feature<Polygon | MultiPolygon, GeoJsonProperties>;

  // Initialize counts with totalPoints
  const counts: ClusterCountsWithTotal = { totalPoints: 0 };

  points.forEach((pointData) => {
    // If filterCluster is defined, skip points not in the specified cluster
    if (filterCluster !== undefined && pointData.clusters !== filterCluster) {
      return;
    }

    const pt: Feature<Point, GeoJsonProperties> = {
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [pointData.longitude, pointData.latitude],
      },
      properties: {},
    };

    if (booleanPointInPolygon(pt, polygon)) {
      counts.totalPoints += 1;
      const cluster = pointData.clusters;
      counts[cluster] = (counts[cluster] || 0) + 1;
    }
  });

  return counts;
};

// Define default options for clarity
interface FitBoundsOptions {
  minZoom?: number;
  maxZoom?: number;
  padding?: number | { top: number; bottom: number; left: number; right: number };
  defaultFallbackState?: ViewStateType; // Fallback if no points or dimensions
}

// Default fallback state if none provided
const DEFAULT_FALLBACK_STATE: ViewStateType = {
  longitude: -96,
  latitude: 38,
  zoom: 3.5,
  minZoom: 3,
  maxZoom: 18, // Allow potentially higher zoom for features
  pitch: 0,
  bearing: 0,
};
/**
 * Calculates a Deck.gl ViewState to fit the given points within the container dimensions.
 * Uses WebMercatorViewport.fitBounds for accurate calculation.
 *
 * @param points - Array of points with longitude and latitude.
 * @param containerWidth - Width of the map container in pixels.
 * @param containerHeight - Height of the map container in pixels.
 * @param options - Optional configuration for zoom limits, padding, and fallback.
 * @returns A ViewStateType object configured to fit the bounds (WITHOUT transition props).
 */
export const getFitBoundsViewState = (
  points: ExportMapData[],
  containerWidth: number,
  containerHeight: number,
  options: FitBoundsOptions = {}
): ViewStateType => {
  const {
    minZoom = 3, // Default min zoom for fitting features
    maxZoom = 18, // Default max zoom for fitting features
    padding = 40, // Default padding in pixels
    defaultFallbackState = DEFAULT_FALLBACK_STATE,
  } = options;

  // --- Input Validation ---
  if (
    !points ||
    points.length === 0 ||
    !containerWidth ||
    containerWidth <= 0 ||
    !containerHeight ||
    containerHeight <= 0
  ) {
    console.warn("getFitBoundsViewState: Invalid input (no points or zero dimensions), returning fallback state.");
    return defaultFallbackState;
  }

  // --- Calculate Bounds ---
  const bounds = points.reduce(
    (acc, point) => {
      if (
        typeof point.longitude !== "number" ||
        typeof point.latitude !== "number" ||
        !isFinite(point.longitude) ||
        !isFinite(point.latitude)
      ) {
        console.warn("getFitBoundsViewState: Skipping invalid point", point);
        return acc;
      }
      acc.minLng = Math.min(acc.minLng, point.longitude);
      acc.maxLng = Math.max(acc.maxLng, point.longitude);
      acc.minLat = Math.min(acc.minLat, point.latitude);
      acc.maxLat = Math.max(acc.maxLat, point.latitude);
      return acc;
    },
    { minLng: Infinity, maxLng: -Infinity, minLat: Infinity, maxLat: -Infinity }
  );

  if (!isFinite(bounds.minLng)) {
    // Check if any valid points were processed
    console.warn("getFitBoundsViewState: No valid points found to calculate bounds, returning fallback state.");
    return defaultFallbackState;
  }

  const boundsWidth = bounds.maxLng - bounds.minLng;
  const boundsHeight = bounds.maxLat - bounds.minLat;
  const buffer = 0.01; // Add a small degree buffer if extent is zero

  const finalBounds: [[number, number], [number, number]] = [
    [bounds.minLng - (boundsWidth === 0 ? buffer : 0), bounds.minLat - (boundsHeight === 0 ? buffer : 0)],
    [bounds.maxLng + (boundsWidth === 0 ? buffer : 0), bounds.maxLat + (boundsHeight === 0 ? buffer : 0)],
  ];

  // --- Use WebMercatorViewport to calculate fit ---
  try {
    const viewport = new WebMercatorViewport({
      width: containerWidth,
      height: containerHeight,
      longitude: (finalBounds[0][0] + finalBounds[1][0]) / 2,
      latitude: (finalBounds[0][1] + finalBounds[1][1]) / 2,
      zoom: 1,
    });

    const { longitude, latitude, zoom } = viewport.fitBounds(finalBounds, {
      padding: padding,
    });

    // --- Clamp Zoom and Construct Final State (NO transition props) ---
    const clampedZoom = Math.max(minZoom, Math.min(maxZoom, zoom));

    return {
      longitude,
      latitude,
      zoom: clampedZoom,
      minZoom: minZoom, // Include min/max if needed by DeckGL view
      maxZoom: maxZoom,
      pitch: 0, // Default pitch
      bearing: 0, // Default bearing
      // REMOVED transition props from return object
    };
  } catch (error) {
    console.error("getFitBoundsViewState: Error during viewport calculation:", error);
    return defaultFallbackState;
  }
};

/**
 * Calculates a Deck.gl ViewState to fit the given geographic bounding box within the container dimensions.
 * Uses WebMercatorViewport.fitBounds for accurate calculation.
 * Intended for fitting initial map views based on GeoJSON extent.
 *
 * @param geoJsonBounds - A bounding box array: [minLng, minLat, maxLng, maxLat].
 * @param containerWidth - Width of the map container in pixels.
 * @param containerHeight - Height of the map container in pixels.
 * @param options - Optional configuration for zoom limits, padding, and fallback.
 * @returns A ViewStateType object configured to fit the bounds (WITHOUT transition props).
 */
export const calculateBoundsViewState = (
  geoJsonBounds: BBox,
  containerWidth: number,
  containerHeight: number,
  options: FitBoundsOptions = {}
): ViewStateType => {
   const {
    // Use potentially different defaults for initial view vs. feature zoom
    minZoom = 3,
    maxZoom = 12, // Max zoom for initial view might be lower
    padding = 20, // Default padding for initial view
    defaultFallbackState = DEFAULT_FALLBACK_STATE,
  } = options;

  // --- Input Validation ---
  if (
    !geoJsonBounds || geoJsonBounds.length !== 4 ||
    !geoJsonBounds.every(coord => typeof coord === 'number' && isFinite(coord)) ||
    !containerWidth || containerWidth <= 0 ||
    !containerHeight || containerHeight <= 0
  ) {
    console.warn("calculateBoundsViewState: Invalid input (bounds or dimensions), returning fallback state.");
    return defaultFallbackState;
  }

  // Ensure bounds are in correct order [minLng, minLat, maxLng, maxLat]
   const [[minLng, minLat], [maxLng, maxLat]] = [
     [geoJsonBounds[0], geoJsonBounds[1]],
     [geoJsonBounds[2], geoJsonBounds[3]]
   ];

   // Check for invalid bounds order
   if (minLng > maxLng || minLat > maxLat) {
        console.warn("calculateBoundsViewState: Invalid bounds order (min > max), returning fallback state.", geoJsonBounds);
        return defaultFallbackState;
   }


  // --- Use WebMercatorViewport to calculate fit ---
  try {
    const viewport = new WebMercatorViewport({
      width: containerWidth,
      height: containerHeight,
      longitude: (minLng + maxLng) / 2, // Estimate center
      latitude: (minLat + maxLat) / 2,
      zoom: 1, // Start low
    });

    // fitBounds expects [[minLng, minLat], [maxLng, maxLat]] format
    const { longitude, latitude, zoom } = viewport.fitBounds(
        [[minLng, minLat], [maxLng, maxLat]],
        { padding }
    );

    // --- Clamp Zoom and Construct Final State (NO transition props) ---
    const clampedZoom = Math.max(minZoom, Math.min(maxZoom, zoom));

    return {
      longitude,
      latitude,
      zoom: clampedZoom,
      minZoom: minZoom, // Include min/max if needed by DeckGL view
      maxZoom: maxZoom,
      pitch: 0, // Default pitch
      bearing: 0, // Default bearing
    };
  } catch (error) {
    console.error("calculateBoundsViewState: Error during viewport calculation:", error);
    return defaultFallbackState;
  }
};

/**
 * Calculates the number of points within the given county's geometry, grouped by cluster,
 * and the total number of points in that county.
 *
 * @param countyFeature - The GeoJSON feature representing the county.
 * @param data - The array of data points.
 * @returns An object mapping cluster numbers to counts and the total point count.
 */
export const calculateAreaCountsForCounty = (
  countyFeature: SafeFeature<Polygon | MultiPolygon>,
  data: ExportMapData[]
): ClusterCountsWithTotal | null => {
  if (!countyFeature.geometry) return null;

  // Initialize counts
  const counts: ClusterCountsWithTotal = { totalPoints: 0 };

  data.forEach((point) => {
    const pt: Feature<Point, GeoJsonProperties> = {
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [point.longitude, point.latitude],
      },
      properties: {},
    };

    // Check if the point is inside the county polygon
    if (booleanPointInPolygon(pt, countyFeature)) {
      counts.totalPoints += 1;
      const cluster = point.clusters;
      counts[cluster] = (counts[cluster] || 0) + 1;
    }
  });

  return counts;
};
